import api from './api';
import {
  WhatsAppProvider,
  ProviderBranchSettings,
  WhatsAppTemplate,
  WhatsAppMessage,
  BulkMessageBatch,
  SendMessageData,
  BulkSendData,
  MessageFilters,
  WhatsAppStatistics,
  TestConnectionData,
  TestConnectionResponse,
  MessageHistoryResponse,
  StatisticsResponse,
  ApiResponse
} from '../types/whatsapp';

const BASE_URL = '/marketing/whatsapp';

/**
 * WhatsApp Service
 * Handles all API calls for WhatsApp functionality
 */
class WhatsAppService {
  // ============================================
  // PROVIDERS MANAGEMENT
  // ============================================

  /**
   * Get all providers
   */
  async getProviders(): Promise<WhatsAppProvider[]> {
    const response = await api.get(`${BASE_URL}/providers`);
    return response.data.providers;
  }

  /**
   * Get provider by ID
   */
  async getProviderById(id: string): Promise<WhatsAppProvider> {
    const response = await api.get(`${BASE_URL}/providers/${id}`);
    return response.data.provider;
  }

  /**
   * Create new provider
   */
  async createProvider(data: Partial<WhatsAppProvider>): Promise<WhatsAppProvider> {
    const response = await api.post(`${BASE_URL}/providers`, data);
    return response.data.provider;
  }

  /**
   * Update provider
   */
  async updateProvider(id: string, data: Partial<WhatsAppProvider>): Promise<WhatsAppProvider> {
    const response = await api.put(`${BASE_URL}/providers/${id}`, data);
    return response.data.provider;
  }

  /**
   * Delete provider
   */
  async deleteProvider(id: string): Promise<void> {
    await api.delete(`${BASE_URL}/providers/${id}`);
  }

  /**
   * Toggle provider status
   */
  async toggleProvider(id: string): Promise<WhatsAppProvider> {
    const response = await api.put(`${BASE_URL}/providers/${id}/toggle`);
    return response.data.provider;
  }

  // ============================================
  // BRANCH SETTINGS
  // ============================================

  /**
   * Get branch settings
   */
  async getBranchSettings(): Promise<ProviderBranchSettings[]> {
    const response = await api.get(`${BASE_URL}/settings`);
    return response.data.settings;
  }

  /**
   * Save/Update branch settings
   */
  async saveBranchSettings(data: Partial<ProviderBranchSettings>): Promise<ProviderBranchSettings> {
    const response = await api.put(`${BASE_URL}/settings`, data);
    return response.data.settings;
  }

  /**
   * Test connection
   */
  async testConnection(data: TestConnectionData): Promise<TestConnectionResponse> {
    const response = await api.post(`${BASE_URL}/settings/test-connection`, data);
    return response.data;
  }

  /**
   * Sync templates
   */
  async syncTemplates(): Promise<ApiResponse<{ count: number }>> {
    const response = await api.post(`${BASE_URL}/settings/sync-templates`);
    return response.data;
  }

  /**
   * Get templates
   */
  async getTemplates(): Promise<WhatsAppTemplate[]> {
    const response = await api.get(`${BASE_URL}/templates`);
    return response.data.templates;
  }

  // ============================================
  // SEND MESSAGES
  // ============================================

  /**
   * Send single message
   */
  async sendMessage(data: SendMessageData): Promise<ApiResponse<{ messageId: string }>> {
    const response = await api.post(`${BASE_URL}/send/single`, data);
    return response.data;
  }

  /**
   * Send bulk messages
   */
  async sendBulkMessages(data: BulkSendData): Promise<ApiResponse<{ batchId: string }>> {
    const response = await api.post(`${BASE_URL}/send/bulk`, data);
    return response.data;
  }

  /**
   * Send bulk messages with unique coupons
   */
  async sendBulkMessagesWithCoupons(data: BulkSendData & { baseCouponId: string }): Promise<ApiResponse<{ batchId: string; coupons?: any[] }>> {
    const response = await api.post(`${BASE_URL}/send/bulk-with-coupons`, data);
    return response.data;
  }

  /**
   * Schedule message
   */
  async scheduleMessage(data: SendMessageData): Promise<ApiResponse<{ messageId: string }>> {
    const response = await api.post(`${BASE_URL}/send/schedule`, data);
    return response.data;
  }

  // ============================================
  // MESSAGE HISTORY
  // ============================================

  /**
   * Get message history
   */
  async getMessageHistory(
    filters: MessageFilters = {},
    page: number = 1,
    limit: number = 50
  ): Promise<MessageHistoryResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = Array.isArray(value) ? value.join(',') : value.toString();
        }
        return acc;
      }, {} as Record<string, string>)
    });

    const response = await api.get(`${BASE_URL}/messages?${params}`);
    return response.data;
  }

  /**
   * Get message by ID
   */
  async getMessageById(id: string): Promise<WhatsAppMessage> {
    const response = await api.get(`${BASE_URL}/messages/${id}`);
    return response.data.message;
  }

  /**
   * Resend failed message
   */
  async resendMessage(id: string): Promise<ApiResponse<{ messageId: string }>> {
    const response = await api.post(`${BASE_URL}/messages/${id}/resend`);
    return response.data;
  }

  /**
   * Delete message
   */
  async deleteMessage(id: string): Promise<void> {
    await api.delete(`${BASE_URL}/messages/${id}`);
  }

  // ============================================
  // ANALYTICS
  // ============================================

  /**
   * Get statistics
   */
  async getStatistics(startDate?: string, endDate?: string): Promise<StatisticsResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`${BASE_URL}/analytics/statistics?${params}`);
    return response.data;
  }

  /**
   * Get bulk batches
   */
  async getBulkBatches(filters?: {
    status?: string;
    campaignType?: string;
    page?: number;
    limit?: number;
  }): Promise<{ batches: BulkMessageBatch[]; pagination: any }> {
    const params = new URLSearchParams({
      page: (filters?.page || 1).toString(),
      limit: (filters?.limit || 20).toString()
    });
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.campaignType) params.append('campaignType', filters.campaignType);

    const response = await api.get(`${BASE_URL}/bulk-batches?${params}`);
    return response.data;
  }

  /**
   * Get bulk batch status
   */
  async getBulkBatchStatus(id: string): Promise<BulkMessageBatch> {
    const response = await api.get(`${BASE_URL}/bulk-batches/${id}`);
    return response.data.batch;
  }

  /**
   * Cancel bulk batch
   */
  async cancelBulkBatch(id: string): Promise<BulkMessageBatch> {
    const response = await api.post(`${BASE_URL}/bulk-batches/${id}/cancel`);
    return response.data.batch;
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Format phone number
   */
  formatPhoneNumber(phoneNumber: string, countryCode: string = '+966'): string {
    // Remove all non-digit characters
    let cleaned = phoneNumber.replace(/\D/g, '');
    
    // Remove leading zeros
    cleaned = cleaned.replace(/^0+/, '');
    
    // Add country code if not present
    const countryDigits = countryCode.replace(/\D/g, '');
    if (!cleaned.startsWith(countryDigits)) {
      cleaned = countryDigits + cleaned;
    }
    
    return cleaned;
  }

  /**
   * Get status color
   */
  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      pending: 'orange',
      queued: 'blue',
      sent: 'cyan',
      delivered: 'green',
      read: 'purple',
      failed: 'red'
    };
    return colors[status] || 'gray';
  }

  /**
   * Get status text in Arabic
   */
  getStatusText(status: string): string {
    const texts: Record<string, string> = {
      pending: 'قيد الانتظار',
      queued: 'في قائمة الانتظار',
      sent: 'تم الإرسال',
      delivered: 'تم التسليم',
      read: 'تمت القراءة',
      failed: 'فشل'
    };
    return texts[status] || status;
  }

  /**
   * Get message type text in Arabic
   */
  getMessageTypeText(type: string): string {
    const texts: Record<string, string> = {
      template: 'قالب',
      text: 'نص',
      media: 'وسائط'
    };
    return texts[type] || type;
  }

  /**
   * Calculate delivery rate
   */
  calculateDeliveryRate(totalSent: number, totalDelivered: number): number {
    if (totalSent === 0) return 0;
    return Math.round((totalDelivered / totalSent) * 100);
  }

  /**
   * Calculate read rate
   */
  calculateReadRate(totalDelivered: number, totalRead: number): number {
    if (totalDelivered === 0) return 0;
    return Math.round((totalRead / totalDelivered) * 100);
  }
}

export default new WhatsAppService();